import { DialogComponent } from './dialog.js';

/**
 * A custom element that manages a cocktail details dialog.
 * Opens when a blog card is clicked and displays cocktail information from metafields.
 *
 * @extends DialogComponent
 */
export class CocktailDetailsDialog extends DialogComponent {
  requiredRefs = ['dialog', 'name', 'content', 'shortDescription', 'level', 'glass', 'garnish', 'ingredients', 'ingredientsList', 'method', 'methodContent', 'closeButton'];

  connectedCallback() {
    super.connectedCallback();
    
    // Listen for custom event to open dialog with cocktail data
    document.addEventListener('cocktail-details:open', this.#handleOpenDialog);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cocktail-details:open', this.#handleOpenDialog);
  }

  /**
   * Handles the custom event to open the dialog with cocktail data.
   * 
   * @param {CustomEvent} event - The custom event containing cocktail data
   */
  #handleOpenDialog = (event) => {
    const { cocktailData } = event.detail;
    
    if (!cocktailData) {
      console.warn('CocktailDetailsDialog: No cocktail data provided');
      return;
    }

    this.populateDialog(cocktailData);
    this.showDialog();
    
    // Focus the close button for accessibility
    requestAnimationFrame(() => {
      this.refs.closeButton?.focus();
    });
  };

  /**
   * Populates the dialog with cocktail data from metafields.
   * 
   * @param {Object} cocktailData - The cocktail data object from metafields
   */
  populateDialog(cocktailData) {
    const {
      name,
      short_description,
      level,
      glass,
      garnish,
      ingredients,
      method
    } = cocktailData;

    // Set name
    if (this.refs.name) {
      this.refs.name.textContent = name || '';
    }

    // Set short description
    if (this.refs.shortDescription) {
      this.refs.shortDescription.textContent = short_description || '';
      this.refs.shortDescription.style.display = short_description ? 'block' : 'none';
    }

    // Set level
    if (this.refs.level) {
      if (level && level.name) {
        const iconHtml = level.icon ? `<img src="${level.icon}" alt="" aria-hidden="true" />` : '';
        this.refs.level.innerHTML = `
          <div class="m-xxs d-xxs">${this.#getLevelLabel()}</div>
          ${iconHtml}
          <span class="m-xs d-xs">${level.name}</span>
        `;
        this.refs.level.style.display = 'flex';
      } else {
        this.refs.level.style.display = 'none';
      }
    }

    // Set glass
    if (this.refs.glass) {
      if (glass && glass.name) {
        const iconHtml = glass.icon ? `<img src="${glass.icon}" alt="" aria-hidden="true" />` : '';
        this.refs.glass.innerHTML = `
          <div class="m-xxs d-xxs">${this.#getGlassLabel()}</div>
          ${iconHtml}
          <span class="m-xs d-xs">${glass.name}</span>
        `;
        this.refs.glass.style.display = 'flex';
      } else {
        this.refs.glass.style.display = 'none';
      }
    }

    // Set garnish
    if (this.refs.garnish) {
      if (garnish && garnish.name) {
        const iconHtml = garnish.icon ? `<img src="${garnish.icon}" alt="" aria-hidden="true" />` : '';
        this.refs.garnish.innerHTML = `
          <div class="m-xxs d-xxs">${this.#getGarnishLabel()}</div>
          ${iconHtml}
          <span class="m-xs d-xs">${garnish.name}</span>
        `;
        this.refs.garnish.style.display = 'flex';
      } else {
        this.refs.garnish.style.display = 'none';
      }
    }

    // Set ingredients
    if (this.refs.ingredients && this.refs.ingredientsList) {
      if (ingredients && Array.isArray(ingredients) && ingredients.length > 0) {
        this.refs.ingredientsList.innerHTML = ingredients
          .map(ingredient => `<li>${this.#escapeHtml(ingredient)}</li>`)
          .join('');
        this.refs.ingredients.style.display = 'block';
      } else {
        this.refs.ingredients.style.display = 'none';
      }
    }

    // Set method
    if (this.refs.method && this.refs.methodContent) {
      if (method) {
        this.refs.methodContent.textContent = method;
        this.refs.method.style.display = 'block';
      } else {
        this.refs.method.style.display = 'none';
      }
    }
  }

  /**
   * Gets the localized label for "Level".
   * 
   * @returns {string} The localized label
   */
  #getLevelLabel() {
    return this.#getLabel('labelLevel', 'Level');
  }

  /**
   * Gets the localized label for "Glass".
   * 
   * @returns {string} The localized label
   */
  #getGlassLabel() {
    return this.#getLabel('labelGlass', 'Glass');
  }

  /**
   * Gets the localized label for "Garnish".
   * 
   * @returns {string} The localized label
   */
  #getGarnishLabel() {
    return this.#getLabel('labelGarnish', 'Garnish');
  }

  /**
   * Reads a label from data attributes with a fallback.
   *
   * @param {string} datasetKey - The dataset key to read
   * @param {string} fallback - Fallback label if missing
   * @returns {string}
   */
  #getLabel(datasetKey, fallback) {
    const label = this.dataset?.[datasetKey];
    return label || fallback;
  }

  /**
   * Escapes HTML to prevent XSS.
   * 
   * @param {string} text - The text to escape
   * @returns {string} The escaped text
   */
  #escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('cocktail-details-dialog')) {
  customElements.define('cocktail-details-dialog', CocktailDetailsDialog);
}
